import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, takeUntil, combineLatest } from 'rxjs';
import { AuthService } from 'src/app/core/auth/auth.service';
import { DataService } from 'src/app/shared/data/data.service';
import { MenuItem, SideBarData, SubMenu } from 'src/app/core/models';
import { AppUser } from 'src/app/core/models/users.model';
import { NotificationService } from 'src/app/core/services/notification.service';
import { NotificationSummary } from 'src/app/core/models/ticket.model';
import { AppRoutes } from 'src/app/shared/routes/routes';
import { SideBarService } from 'src/app/shared/side-bar/side-bar.service';
import { PermissionsUtils } from 'src/app/shared/utils/permissions.utils';
import { FeatureFlagsV2Service } from 'src/app/core/services/feature-flags.v2.service';
import { LocationContextService } from 'src/app/core/services/location-context.service';
import {
  ReportCard,
  reportCardsBillingCollections,
  reportCardsClinical,
  reportCardsHumanResources,
  reportCardsManagement,
  reportCardsOperations,
} from 'src/app/pages/reports/reports-access/const/reports-cards.const';

const REPORT_CARDS: ReportCard[] = [
  ...reportCardsOperations,
  ...reportCardsBillingCollections,
  ...reportCardsHumanResources,
  ...reportCardsManagement,
  ...reportCardsClinical,
];

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  routes = AppRoutes;
  base = '';
  page = '';
  currentUrl = '';
  classAdd = false;

  multilevel = [false, false, false];

  sidebarData: SideBarData[] = [];
  user!: AppUser;
  notificationSummary: NotificationSummary = {
    unread_tickets_count: 0,
    unread_responses_count: 0,
    new_tickets_count: 0,
    total_unread: 0
  };

  private destroy$ = new Subject<void>();
  private readonly data = inject(DataService);
  private readonly router = inject(Router);
  private readonly sideBar = inject(SideBarService);
  private readonly authService = inject(AuthService);
  private readonly notificationService = inject(NotificationService);
  private readonly featureFlagsService = inject(FeatureFlagsV2Service);
  private readonly locationContextService = inject(LocationContextService);

  constructor() {
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.getRoutes(event);
        }
      });
    this.getRoutes(this.router);
  }

  ngOnInit(): void {
    // Subscribe to both user and feature flag changes
    combineLatest([
      this.authService.user$,
      this.featureFlagsService.getAllFlags$(),
      this.locationContextService.isAdminViewMode$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([user]) => {
        this.user = user as AppUser;
        this.updateSidebar();
      });

    // Subscribe to notification updates
    this.notificationService.getNotificationSummary()
      .pipe(takeUntil(this.destroy$))
      .subscribe(summary => {
        this.notificationSummary = summary;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateSidebar(): void {
    if (!this.user) {
      this.sidebarData = [];
      return;
    }

    const permissions = this.user?.permissions || [];
    const isAdminViewMode = this.locationContextService.isAdminViewMode();

    if (this.user?.roles.includes('SUPERADMIN')) {
      // Admin users see all menu items, but still respect show_nav property
      const fullSidebar: SideBarData[] = [];

      this.data.sidebar.forEach((section: SideBarData) => {
        const filteredSection: SideBarData = { ...section, menu: [] as MenuItem[] };

        section.menu.forEach((menuItem: MenuItem) => {
          const canShowReportsMenu = !this.isReportsMenu(menuItem) || this.canAccessAnyReport(permissions);
          const matchesCurrentView = this.shouldShowMenuForCurrentView(menuItem, isAdminViewMode);
          // For admin users, check show_nav and feature flags
          const shouldShowMenuItem = menuItem.show_nav !== false &&
                                    this.shouldShowBasedOnFeatureFlag(menuItem) &&
                                    canShowReportsMenu &&
                                    matchesCurrentView;

          if (shouldShowMenuItem) {
            if (menuItem.hasSubRoute && menuItem.subMenus?.length > 0) {
              // Filter submenus based on show_nav and feature flags
              const filteredSubMenus = menuItem.subMenus.filter(submenu =>
                submenu.show_nav !== false &&
                this.shouldShowBasedOnFeatureFlag(submenu)
              );

              if (filteredSubMenus.length > 0) {
                const itemCopy = { ...menuItem, subMenus: filteredSubMenus };
                filteredSection.menu.push(itemCopy);
              }
            } else {
              filteredSection.menu.push({ ...menuItem });
            }
          }
        });

        if (filteredSection.menu.length > 0) {
          fullSidebar.push(filteredSection);
        }
      });

      this.sidebarData = this.applyLocationSettingsMenuRules(fullSidebar);
    } else {
      // For regular users, filter menu items based on permissions
      const filteredSidebar: SideBarData[] = [];

      this.data.sidebar.forEach((section: SideBarData) => {
        // Create a deep copy of the section to modify
        const filteredSection: SideBarData = { ...section, menu: [] as MenuItem[] };

        section.menu.forEach((menuItem: MenuItem) => {
          // Check if menu item should be visible based on permission, show_nav, and feature flags
          const hasRequiredPermission = this.hasMenuPermission(menuItem, permissions);
          const matchesCurrentView = this.shouldShowMenuForCurrentView(menuItem, isAdminViewMode);
          const shouldShowMenuItem = menuItem.show_nav !== false &&
                                    this.shouldShowBasedOnFeatureFlag(menuItem) &&
                                    matchesCurrentView;

          if (hasRequiredPermission && shouldShowMenuItem) {
            // For parent items with submenus, filter submenus based on permissions
            if (menuItem.hasSubRoute && menuItem.subMenus?.length > 0) {
              const filteredSubMenus = menuItem.subMenus.filter(submenu =>
                // Show submenu if it has no permission requirement or user has the required permission
                // Also respect the show_nav property and feature flags
                (!submenu.permision || permissions.includes(submenu.permision)) &&
                submenu.show_nav !== false &&
                this.shouldShowBasedOnFeatureFlag(submenu)
              );

              // Only include this menu item if it has at least one visible submenu
              if (filteredSubMenus.length > 0) {
                const itemCopy = { ...menuItem, subMenus: filteredSubMenus };
                filteredSection.menu.push(itemCopy);
              }
            } else {
              // For menu items without submenus, include them directly
              filteredSection.menu.push({ ...menuItem });
            }
          }
        });

        // Only include sections that have at least one menu item
        if (filteredSection.menu.length > 0) {
          filteredSidebar.push(filteredSection);
        }
      });

      this.sidebarData = this.applyLocationSettingsMenuRules(filteredSidebar);
    }
  }

  private applyLocationSettingsMenuRules(sidebar: SideBarData[]): SideBarData[] {
    const currentLocationId = this.locationContextService.getCurrentLocationId();

    return sidebar
      .map((section: SideBarData) => {
        const resolvedMenus = section.menu
          .map((menuItem) => this.resolveLocationSettingsMenuItem(menuItem, currentLocationId))
          .filter((menuItem): menuItem is MenuItem => menuItem !== null);

        return {
          ...section,
          menu: resolvedMenus,
        };
      })
      .filter((section: SideBarData) => section.menu.length > 0);
  }

  private resolveLocationSettingsMenuItem(
    menuItem: MenuItem,
    currentLocationId: number | null
  ): MenuItem | null {
    if (menuItem.menuValue !== 'Loc. Settings') {
      return menuItem;
    }

    if (currentLocationId === null) {
      return null;
    }

    return {
      ...menuItem,
      route: `${AppRoutes.location.edit}/${currentLocationId}`,
    };
  }

  expandSubMenus(menu: MenuItem): void {
    sessionStorage.setItem('menuValue', menu.menuValue);
    this.sidebarData.map((mainMenus: SideBarData) => {
      mainMenus.menu.map((resMenu: MenuItem) => {
        if (resMenu.menuValue === menu.menuValue) {
          menu.showSubRoute = !menu.showSubRoute;
        } else {
          resMenu.showSubRoute = false;
        }
      });
    });
  }
  collapseSubMenus(menu: MenuItem): void {
    sessionStorage.removeItem('menuValue');
    this.sidebarData.map((mainMenus: SideBarData) => {
      mainMenus.menu.map((resMenu: MenuItem) => {
        if (resMenu.menuValue === menu.menuValue) {
          menu.showSubRoute = !menu.showSubRoute;
        } else {
          resMenu.showSubRoute = false;
        }
      });
    });
  }

  private getRoutes(route: { url: string }): void {
    this.currentUrl = route.url;

    const splitVal = route.url.split('/');

    this.base = splitVal[1];
    this.page = splitVal[2];
  }

  handleMobileNavigation(): void {
    this.sideBar.closeMobileSidebarIfMobileView();
  }
  miniSideBarMouseHover(position: string): void {
    if (position === 'over') {
      this.sideBar.expandSideBar.next('true');
    } else {
      this.sideBar.expandSideBar.next('false');
    }
  }

  logout() {
    this.authService.logout().subscribe(() => {
      this.router.navigate([AppRoutes.auth.login]);
    });
  }

  hasPermission(permission: string): boolean {
    return PermissionsUtils.hasPermission(this.user?.permissions || [], permission);
  }

  getTicketNotificationCount(): number {
    // For staff users with manage_tickets permission, show total unread count
    if (this.hasPermission('manage_tickets')) {
      return this.notificationSummary.total_unread;
    }
    return 0;
  }

  isTicketsMenu(menu: MenuItem): boolean {
    return menu.menuValue === 'Tickets';
  }

  private shouldShowMenuForCurrentView(menu: MenuItem, isAdminViewMode: boolean): boolean {
    const viewMode = menu.viewMode ?? 'location';

    if (isAdminViewMode) {
      return viewMode === 'admin' || viewMode === 'both';
    }

    return viewMode === 'location' || viewMode === 'both';
  }

  private hasMenuPermission(menuItem: MenuItem, permissions: string[]): boolean {
    if (this.isReportsMenu(menuItem)) {
      return this.canAccessAnyReport(permissions);
    }

    return !menuItem.permision || permissions.includes(menuItem.permision);
  }

  private isReportsMenu(menuItem: MenuItem): boolean {
    return menuItem.route === AppRoutes.reports.access;
  }

  private canAccessAnyReport(permissions: string[]): boolean {
    return REPORT_CARDS.some((card) => {
      const hasCardPermission = !card.permissions || card.permissions.length === 0
        ? true
        : PermissionsUtils.hasPermission(permissions, card.permissions);

      return hasCardPermission && this.shouldShowBasedOnFeatureFlag(card);
    });
  }

  /**
   * Check if a menu item should be displayed based on its feature flag
   * @param item Menu item to check
   * @returns true if item should be displayed, false otherwise
   */
  private shouldShowBasedOnFeatureFlag(item: MenuItem | SubMenu | ReportCard): boolean {
    // If no feature flag is specified, always show the item
    if (!item.featureFlag) {
      return true;
    }

    // If feature flag is specified, check if it's enabled
    return this.featureFlagsService.isEnabledSync(item.featureFlag);
  }
}
